import { Controller } from "stimulus"

// Breathing room between the theme's fixed top bar and the pinned save bar.
const PIN_GAP = 8

// Drives the "Save as Draft" / "Save and Publish" bar at the top of the V2 menu
// editor. The bar appears as soon as the menu holds changes the published copy
// has not caught up with -- either because the page loaded that way, or because
// the user edited something while it was open.
//
// Edits on this page are written as they happen: the item drawer, the category
// links, the featured/availability toggles and the drag handles all persist
// straight to the draft. So the bar is a way out of the editor rather than a
// buffer holding unsaved work. "Save as Draft" flushes the one form that can
// still be sitting on uncommitted input -- Default Settings -- and returns to
// the menu list. "Save and Publish" does the same, then submits the v2_publish
// form, which redirects back to the list on its own.
export default class extends Controller {
  static targets = ["anchor", "bar", "button", "publishForm", "status"]
  static values = {
    dirty: Boolean,
    indexUrl: String,
  }

  connect() {
    this.busy = false
    this.markDirty = this.markDirty.bind(this)
    this.handleAjaxSuccess = this.handleAjaxSuccess.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleViewportChange = this.handleViewportChange.bind(this)
    this.pinFrame = null

    // Listening on document rather than the controller element: the category
    // and appearance modals render outside the editor shell, so their remote
    // submissions would not bubble through it.
    document.addEventListener("menu-v2:changed", this.markDirty)
    document.addEventListener("ajax:success", this.handleAjaxSuccess)
    document.addEventListener("submit", this.handleSubmit)
    window.addEventListener("scroll", this.handleViewportChange, { passive: true })
    window.addEventListener("resize", this.handleViewportChange)

    this.captureDefaultSettingsSnapshot()
    if (this.dirtyValue) this.showBar()
  }

  disconnect() {
    document.removeEventListener("menu-v2:changed", this.markDirty)
    document.removeEventListener("ajax:success", this.handleAjaxSuccess)
    document.removeEventListener("submit", this.handleSubmit)
    window.removeEventListener("scroll", this.handleViewportChange)
    window.removeEventListener("resize", this.handleViewportChange)
    if (this.pinFrame) cancelAnimationFrame(this.pinFrame)
  }

  // When Default Settings is saved by its own button the values on screen are
  // the values being persisted, so the snapshot moves with them -- otherwise the
  // bar would re-post an already-saved form on the way out.
  handleSubmit(event) {
    if (event.target !== this.defaultSettingsForm()) return

    this.captureDefaultSettingsSnapshot()
  }

  // rails-ujs fires ajax:success for every remote link on the page, including
  // the GETs that only open a modal. Only writes count as a change.
  handleAjaxSuccess(event) {
    if (this.requestMethodFor(event.target) === "get") return

    this.markDirty()
  }

  requestMethodFor(element) {
    if (!element || !element.getAttribute) return "get"

    const method = element.dataset.method || element.getAttribute("method") || "get"
    return method.toLowerCase()
  }

  markDirty() {
    this.dirtyValue = true
    this.showBar()
  }

  showBar() {
    if (!this.hasBarTarget) return

    this.barTarget.hidden = false
    this.updatePinnedState()
  }

  handleViewportChange() {
    if (this.pinFrame) return

    this.pinFrame = requestAnimationFrame(() => {
      this.pinFrame = null
      this.updatePinnedState()
    })
  }

  // The bar pins with position: fixed rather than position: sticky, because the
  // admin theme clips with overflow: hidden on both .wrapper and .content-page,
  // which leaves a sticky descendant inert. The theme's own top bar is fixed for
  // the same reason, so its height is what this one has to clear.
  //
  // While pinned the anchor keeps holding the bar's height, so the anchor's own
  // top stays the honest answer to "has the bar's place in the page scrolled
  // away" -- and it gives the pinned bar its horizontal box for free.
  updatePinnedState() {
    if (!this.hasBarTarget || !this.hasAnchorTarget || this.barTarget.hidden) return

    const offset = this.pinOffset()
    const box = this.anchorTarget.getBoundingClientRect()
    const pinned = this.barTarget.classList.contains("is-pinned")

    if (box.top < offset) {
      if (!pinned) {
        // Reserved while the bar is still in flow -- going fixed first would
        // shrink it to its content width and measure the wrong height.
        this.anchorTarget.style.height = `${this.barTarget.offsetHeight}px`
        this.barTarget.classList.add("is-pinned")
      }

      this.barTarget.style.top = `${offset}px`
      this.barTarget.style.left = `${box.left}px`
      this.barTarget.style.width = `${box.width}px`

      // Re-measured rather than frozen at pin time: a narrower viewport wraps
      // the bar onto more rows, and the gap it leaves behind has to follow.
      const reserved = `${this.barTarget.offsetHeight}px`
      if (this.anchorTarget.style.height !== reserved) this.anchorTarget.style.height = reserved
    } else if (pinned) {
      this.barTarget.classList.remove("is-pinned")
      this.barTarget.style.top = ""
      this.barTarget.style.left = ""
      this.barTarget.style.width = ""
      this.anchorTarget.style.height = ""
    }
  }

  pinOffset() {
    const themeTopBar = document.querySelector(".navbar-custom")
    const height = themeTopBar ? themeTopBar.getBoundingClientRect().height : 0

    return height + PIN_GAP
  }

  async saveDraft(event) {
    event.preventDefault()
    if (!this.lock()) return

    this.setStatus("Saving…")

    if (!(await this.flushDefaultSettings())) {
      this.setStatus("Could not save your default settings. Please try again.", true)
      this.unlock()
      return
    }

    window.location.assign(this.indexUrlValue)
  }

  async saveAndPublish(event) {
    event.preventDefault()
    if (!this.lock()) return

    this.setStatus("Publishing…")

    if (!(await this.flushDefaultSettings())) {
      this.setStatus("Could not save your default settings. Please try again.", true)
      this.unlock()
      return
    }

    // Submitted straight off the element so this handler does not intercept its
    // own submit. v2_publish enqueues the publish and redirects to the list.
    this.publishFormTarget.submit()
  }

  // Every other edit here has already been written by the time the user reaches
  // the bar; Default Settings is the one form with a manual save, so its
  // pending input is flushed before we leave the page.
  async flushDefaultSettings() {
    const form = this.defaultSettingsForm()
    if (!form || !this.defaultSettingsChanged(form)) return true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        body: new FormData(form),
      })
      return response.ok
    } catch (_error) {
      return false
    }
  }

  captureDefaultSettingsSnapshot() {
    const form = this.defaultSettingsForm()
    this.defaultSettingsSnapshot = form ? this.serializeDefaultSettings(form) : null
  }

  defaultSettingsChanged(form) {
    return this.serializeDefaultSettings(form) !== this.defaultSettingsSnapshot
  }

  serializeDefaultSettings(form) {
    return Array.from(form.querySelectorAll("[data-default-key]"))
      .map((field) => `${field.dataset.defaultKey}=${field.value}`)
      .join("&")
  }

  defaultSettingsForm() {
    return document.querySelector("[data-default-settings-form]")
  }

  lock() {
    if (this.busy) return false

    this.busy = true
    this.buttonTargets.forEach((button) => { button.disabled = true })
    return true
  }

  unlock() {
    this.busy = false
    this.buttonTargets.forEach((button) => { button.disabled = false })
  }

  setStatus(message, isError = false) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("is-error", isError)
  }
}
